import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { QUIZ } from "@/constants/testIds";

export default function Quiz() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quiz/questions");
        setQuestions(data.questions);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white grid place-items-center">
        <div className="text-slate-500">Loading…</div>
      </div>
    );
  }

  const q = questions[step];
  const progress = ((step + (answers[q?.id] ? 1 : 0)) / questions.length) * 100;
  const isLast = step === questions.length - 1;
  const selected = answers[q?.id];

  function select(value) {
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
  }

  function next() {
    if (isLast) {
      sessionStorage.setItem("quiz_answers", JSON.stringify(answers));
      navigate("/results");
    } else {
      setStep((s) => s + 1);
    }
  }

  function back() {
    if (step === 0) navigate("/");
    else setStep((s) => s - 1);
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main
        data-testid={QUIZ.container}
        className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-6 py-10 lg:py-16"
      >
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e]">
              Question {step + 1} of {questions.length}
            </span>
            <span className="text-xs text-slate-500">{Math.round(progress)}% complete</span>
          </div>
          <Progress
            value={progress}
            data-testid={QUIZ.progress}
            className="h-2 bg-slate-100 [&>div]:bg-[#0f766e]"
          />
        </div>

        <div key={q.id} className="fade-in-up">
          <h1
            data-testid={QUIZ.questionTitle}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
          >
            {q.title}
          </h1>
          {q.subtitle && (
            <p className="mt-3 text-base text-slate-600 leading-relaxed">{q.subtitle}</p>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {q.options.map((opt) => {
              const active = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={QUIZ.optionBtn(q.id, opt.value)}
                  onClick={() => select(opt.value)}
                  className={`text-left p-5 border rounded-xl transition-all duration-200 group ${
                    active
                      ? "border-[#0f766e] bg-[#ccfbf1]"
                      : "border-slate-200 bg-white hover:border-[#0f766e] hover:-translate-y-0.5"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-semibold text-slate-900">
                        {opt.label}
                      </div>
                      {opt.hint && (
                        <div className="text-sm text-slate-500 mt-1">{opt.hint}</div>
                      )}
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 mt-1 ${
                        active ? "border-[#0f766e] bg-[#0f766e]" : "border-slate-300"
                      }`}
                    >
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={back}
            data-testid={QUIZ.backBtn}
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={next}
            disabled={!selected}
            data-testid={QUIZ.nextBtn}
            className="bg-[#0f766e] hover:bg-[#115e59] text-white h-11 px-6 disabled:opacity-40"
          >
            {isLast ? "See my matches" : "Next"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
